import { format, parse } from "date-fns";
import { uuid } from "vue-uuid";
import type { StripeChargePayload, StripeIntentPayload } from "@/composables/types/stripe";
import { dollarsToCents } from "@/mixins/helpers";
import { useCapitalizeWords } from "@/util/string";
import type { FormIoApplicationSubmission } from "../components/RegistrationProcess/types/formio";
import type { PaymentServicePayload } from "../components/RegistrationProcess/types/paymentService";
import type { DepositModelData } from "./types/deposit";

export const useDepositModels = () => {
  const getSubmissionsDataForFormIo = (data: DepositModelData): FormIoApplicationSubmission => {
    const {
      person,
      applicationId,
      billingAddress,
      currentAddress,
      permanentAddress,
      promoCode,
      transactionId,
      completedManually,
      creditTransactionId,
      cardExpMonth,
      cancellationPolicy,
    } = data;
    return {
      data: {
        firstname: person.first_name,
        lastname: person.last_name,
        address: billingAddress.street_address_1,
        city: billingAddress.city,
        state: billingAddress.state,
        country: billingAddress.country,
        postCode: billingAddress.postal_code,
        currentAddress: currentAddress.street_address_1,
        currentCity: currentAddress.city,
        currentState: currentAddress.state,
        currentCountry: currentAddress.country,
        currentPostCode: currentAddress.postal_code,
        permanentAddress: permanentAddress.street_address_1,
        permanentCity: permanentAddress.city,
        permanentState: permanentAddress.state,
        permanentCountry: permanentAddress.country,
        permanentPostCode: permanentAddress.postal_code,
        promotionCode: promoCode ?? "",
        expMonth: cardExpMonth,
        cancellationPolicy: cancellationPolicy,
        creditTransactionId: creditTransactionId ?? "",
        application_id: applicationId,
        transactionId: transactionId,
        completedManually: completedManually ?? false,
      },
    };
  };

  const mapFields = (session: any) => {
    return {
      program_id: session.parent?.salesforce_id ?? "",
      is_customized: session.channel_id === 2,
      program_session_id: session.salesforce_id,
      program_name: session.name,
      program_session: session.terms?.[0]?.session ?? null,
      start_date: session.session_travel_details?.[0]?.arrival_date ?? session.academic_start_date,
      end_date: session.session_travel_details?.[0]?.departure_date ?? session.academic_end_date,
      start_date_is_final: session.session_travel_details?.[0]?.arrival_date_is_final ?? false,
      end_date_is_final: session.session_travel_details?.[0]?.departure_date_is_final ?? false,
      program_city: session.session_travel_details?.[0]?.arrival_city?.city ?? null,
      program_country: session.session_travel_details?.[0]?.arrival_city?.country?.name ?? null,
      financial_product_line_id: session?.financial_product_line_id,
      financial_product_type_id: session?.financial_product_type_id,
      session_id: session?.id,
    };
  };

  const birthdayFormat = (date: string) => {
    return format(parse(date, "MM/dd/yyyy", new Date()), "yyyy-MM-dd");
  };

  const sessionArrivalYear = (session: any): string => {
    const arrival_dates: string[] = session.session_travel_details?.map((details) => details.arrival_date) ?? [
      session.academic_start_date,
    ];

    const years: number[] = arrival_dates.map((arrivalDate) =>
      Number(format(parse(arrivalDate, "yyyy-MM-dd", new Date()), "yyyy")),
    );

    const arrival_date = Math.min(...years);
    return `${arrival_date}`;
  };

  const getStripeIntentPayload = (data: DepositModelData): StripeIntentPayload => {
    const {
      profile,
      applicationId,
      amount,
      productName,
      netsuiteSku,
      session,
      userData,
      ipeds,
      billingAddress,
      permanentAddress,
      customerId,
      hostInstitution,
      contractNumber,
      promoCode,
      participantId,
      special_pricing_override_entity_id,
    } = data;
    const {
      program_id,
      is_customized,
      program_session_id,
      program_name,
      program_session,
      start_date,
      end_date,
      start_date_is_final,
      end_date_is_final,
      program_city,
      program_country,
      financial_product_line_id,
      financial_product_type_id,
      session_id,
    } = mapFields(session);
    return {
      first_name: profile?.legal_first_name,
      last_name: profile?.last_name,
      email: userData.data.email,
      phone: userData.data.phone,
      birth_date: birthdayFormat(userData.data.birthday),
      participant_type: userData.data.student_type,
      home_university_name: userData.data.schoolname.label ?? "",
      home_institution_ipeds: ipeds,
      application_id: applicationId,
      legacy_participant_account_id: customerId,

      billing_street: billingAddress.street_address_1,
      billing_city: billingAddress.city,
      billing_state_province: billingAddress.state,
      billing_zip_postal_code: billingAddress.postal_code,
      billing_country: billingAddress.country,

      permanent_street: permanentAddress.street_address_1,
      permanent_city: permanentAddress.city,
      permanent_state_province: permanentAddress.state,
      permanent_zip_postal_code: permanentAddress.postal_code,

      netsuite_product_sku: netsuiteSku,
      product_name: useCapitalizeWords(productName),
      price: dollarsToCents(amount),

      program_id: program_id,
      program_session_id: program_session_id,
      program_name: program_name,
      session: program_session,
      year: sessionArrivalYear(session),
      start_date: start_date,
      start_date_is_final: start_date_is_final,
      end_date: end_date,
      end_date_is_final: end_date_is_final,
      program_city: program_city,
      program_country: program_country,
      host_institution: hostInstitution,
      program_session_finance_code: null, // Assuming this is no longer needed
      contract_number: contractNumber,
      promo_code: promoCode ?? "",
      is_application_fee: true,
      is_institution_pays: false,
      is_customized: is_customized,
      participant_id: participantId,
      version: "v3",
      financial_product_line_id,
      financial_product_type_id,
      session_id,
      special_pricing_override_entity_id,
    };
  };

  const getStripeChargePayload = (data: DepositModelData): StripeChargePayload => {
    const {
      person,
      applicationId,
      amount,
      userData,
      ownerId,
      billingAddress,
      currentAddress,
      permanentAddress,
      transactionId,
      paymentStatus,
    } = data;
    return {
      applicationId: applicationId,

      ownerId: ownerId,
      firstname: person.first_name,
      email: userData.data.email,
      lastname: person.last_name,

      address: billingAddress.street_address_1,
      city: billingAddress.city,
      state: billingAddress.state,
      postcode: billingAddress.postal_code,
      country: billingAddress.country,

      currentAddress: currentAddress.street_address_1,
      currentCity: currentAddress.city,
      currentState: currentAddress.state,
      currentPostcode: currentAddress.postal_code,
      currentCountry: currentAddress.country,

      permanentAddress: permanentAddress.street_address_1,
      permanentCity: permanentAddress.city,
      permanentState: permanentAddress.state,
      permanentPostcode: permanentAddress.postal_code,
      permanentCountry: permanentAddress.country,
      stripeClientSecret: transactionId,
      status: paymentStatus,

      amount: dollarsToCents(amount),
    };
  };

  const getPaymentServicePayload = (data: DepositModelData): PaymentServicePayload => {
    const {
      person,
      applicationId,
      amount,
      productName,
      session,
      userData,
      profile,
      ipeds,
      billingAddress,
      currentAddress,
      permanentAddress,
      customerId,
      hostInstitution,
      contractNumber,
      promoCode,
      participantId,
      cancellationPolicy,
      special_pricing_override_entity_id,
    } = data;
    const {
      program_id,
      is_customized,
      program_session_id,
      program_name,
      program_session,
      start_date,
      end_date,
      start_date_is_final,
      end_date_is_final,
      program_city,
      program_country,
      financial_product_line_id,
      financial_product_type_id,
      session_id,
    } = mapFields(session);
    return {
      event_id: `evt_${uuid.v4()}`,
      program_session_finance_code: null,
      legacy_participant_account_id: customerId,
      program_type: session.session_types?.[0]?.name ?? null,
      program_name: program_name,
      program_session: program_session,
      session_country: program_country,
      session_year: sessionArrivalYear(session),
      source_type: "connect",
      amount_in_cents: dollarsToCents(amount),
      application_id: applicationId,
      resource_id: userData._id,
      event_date: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
      event_data: {
        first_name: person.first_name,
        last_name: person.last_name,
        email: profile.email,
        phone: profile.phone,
        participant_type: userData.data.student_type,
        birth_date: birthdayFormat(userData.data.birthday),
        home_university_name: userData.data?.schoolname?.label,
        home_institution_ipeds: ipeds,
        program_city: program_city,
        program_country: program_country,
        program_id: program_id,
        program_session_id: program_session_id,
        year: sessionArrivalYear(session),
        start_date: start_date,
        end_date: end_date,
        end_date_is_final: end_date_is_final,
        start_date_is_final: start_date_is_final,
        host_institution: hostInstitution,
        current_address: currentAddress.street_address_1,
        current_city: currentAddress.city,
        current_state: currentAddress.state,
        current_country: currentAddress.country,
        current_post_code: currentAddress.postal_code,
        permanent_street: permanentAddress.street_address_1,
        permanent_city: permanentAddress.city,
        permanent_state_province: permanentAddress.state,
        permanent_country: permanentAddress.country,
        permanent_zip_postal_code: permanentAddress.postal_code,
        cancellation_policy: cancellationPolicy,
        contract_number: contractNumber,
        product_name: useCapitalizeWords(productName),
        price: amount,
        session: program_session,
        promo_code: promoCode,
        is_application_fee: true,
        is_institution_pays: false,
        legacy_participant_account_id: customerId,
        is_customized: is_customized,
        participant_id: participantId,
        application_id: applicationId,
        billing_street: billingAddress.street_address_1,
        billing_city: billingAddress.city,
        billing_state_province: billingAddress.state,
        billing_zip_postal_code: billingAddress.postal_code,
        version: "v3",
        financial_product_line_id,
        financial_product_type_id,
        session_id,
        special_pricing_override_entity_id,
      },
    };
  };

  return { getSubmissionsDataForFormIo, getStripeIntentPayload, getStripeChargePayload, getPaymentServicePayload };
};
